import { withAnalytics } from '@/lib/analytics';

const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'email_source',
] as const;

const BASE_ANNOTATION = { platform: 'web-v4' } as const;

// ─── Persistence config ──────────────────────────────────────────────────────
// First-party cookie so the backend/CRM can also read it, shared across tabs +
// subdomains, with a FIXED window anchored to capture time (NOT extended by
// activity). The window defaults to 1 day but can be overridden per link via a
// `utm_window` query param (e.g. ?utm_source=x&utm_window=2 → 2-day window),
// capped for safety. Model: capture UTMs, keep first-touch, overwrite last-touch,
// and apply them to every event until the window elapses — then stop.
const COOKIE_NAME = 'oi_attribution';
const DAY_SECONDS = 24 * 60 * 60;
const DEFAULT_MAX_AGE_DAYS = 1;
// Upper bound so a stray/abusive `utm_window` value can't create a near-permanent cookie.
const MAX_WINDOW_DAYS = 90;
// Query param that overrides the tracking window length (in days).
const WINDOW_PARAM = 'utm_window';
// First-touch keys are annotated with this prefix (e.g. first_utm_source).
const FIRST_TOUCH_PREFIX = 'first_';

type CampaignParams = Record<string, string>;

type AttributionData = {
  /** First-touch params — written once, never overwritten. */
  first: CampaignParams;
  /** Last-touch params — overwritten on each new campaign visit. */
  last: CampaignParams;
  firstAt: number;
  lastAt: number;
  /** Window length (seconds) from the `utm_window` param at capture; defaults to 1 day. */
  maxAgeSeconds: number;
};

// ─── Pure parsers ──────────────────────────────────────────────────────────────

function pickCampaignParams(entries: Record<string, string>): CampaignParams | null {
  const out: CampaignParams = {};
  for (const key of CAMPAIGN_KEYS) {
    const value = entries[key]?.trim();
    if (value) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : null;
}

function queryStringToRecord(queryString: string): Record<string, string> {
  return String(queryString ?? '')
    .replace(/^\?/, '')
    .split('&')
    .map(part => part.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, pair) => {
      const [rawKey, ...rest] = pair.split('=');
      const key = decodeURIComponent(String(rawKey ?? '').trim()).toLowerCase();
      if (!key) return acc;
      acc[key] = decodeURIComponent(rest.join('=') || '');
      return acc;
    }, {});
}

/**
 * Tracking-window length (seconds) from an optional `utm_window` query param.
 * Defaults to 1 day; accepts positive numbers (fractional allowed), capped at
 * MAX_WINDOW_DAYS. Invalid/missing values fall back to the default.
 */
function windowSecondsFromRecord(record: Record<string, string>): number {
  const raw = record[WINDOW_PARAM];
  const days = raw != null && raw !== '' ? Number(raw) : NaN;
  const safeDays =
    Number.isFinite(days) && days > 0 ? Math.min(days, MAX_WINDOW_DAYS) : DEFAULT_MAX_AGE_DAYS;
  return Math.round(safeDays * DAY_SECONDS);
}

export function campaignAttributionFromQueryString(queryString: string): CampaignParams | null {
  return pickCampaignParams(queryStringToRecord(queryString));
}

export function campaignAttributionFromUrl(url: string): CampaignParams | null {
  const queryIndex = url.indexOf('?');
  if (queryIndex < 0) return null;
  return campaignAttributionFromQueryString(url.slice(queryIndex + 1));
}

export function campaignAttributionFromRecord(
  data: Record<string, unknown>,
): CampaignParams | null {
  const entries: Record<string, string> = {};
  for (const key of CAMPAIGN_KEYS) {
    const raw = data[key] ?? data[key.toUpperCase()];
    if (typeof raw === 'string' && raw.trim()) entries[key] = raw.trim();
  }
  return pickCampaignParams(entries);
}

// ─── Cookie helpers ──────────────────────────────────────────────────────────

/** Share across *.openigloo.com; host-only for localhost / preview / staging. */
function cookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const match = window.location.hostname.match(/(?:^|\.)(openigloo\.com)$/i);
  return match ? `.${match[1]}` : undefined;
}

function readRawCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1] ?? '') : null;
}

function writeRawCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${maxAgeSeconds}`,
    'samesite=lax',
  ];
  const domain = cookieDomain();
  if (domain) parts.push(`domain=${domain}`);
  if (window.location.protocol === 'https:') parts.push('secure');
  document.cookie = parts.join('; ');
}

function deleteRawCookie(name: string): void {
  if (typeof document === 'undefined') return;
  const parts = [`${name}=`, 'path=/', 'max-age=0'];
  const domain = cookieDomain();
  if (domain) parts.push(`domain=${domain}`);
  document.cookie = parts.join('; ');
}

// ─── Persisted attribution state ──────────────────────────────────────────────

function sanitizeParams(obj: unknown): CampaignParams | null {
  if (!obj || typeof obj !== 'object') return null;
  const rec = obj as Record<string, unknown>;
  const out: CampaignParams = {};
  for (const key of CAMPAIGN_KEYS) {
    const value = rec[key];
    if (typeof value === 'string' && value.trim()) out[key] = value.trim();
  }
  return Object.keys(out).length > 0 ? out : null;
}

function readData(): AttributionData | null {
  const raw = readRawCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AttributionData>;
    const first = sanitizeParams(parsed?.first);
    const last = sanitizeParams(parsed?.last);
    if (!first && !last) return null;
    return {
      first: first ?? last ?? {},
      last: last ?? first ?? {},
      firstAt: typeof parsed?.firstAt === 'number' ? parsed.firstAt : Date.now(),
      lastAt: typeof parsed?.lastAt === 'number' ? parsed.lastAt : Date.now(),
      maxAgeSeconds:
        typeof parsed?.maxAgeSeconds === 'number' && parsed.maxAgeSeconds > 0
          ? parsed.maxAgeSeconds
          : DEFAULT_MAX_AGE_DAYS * DAY_SECONDS,
    };
  } catch {
    return null;
  }
}

function writeData(data: AttributionData): void {
  writeRawCookie(COOKIE_NAME, JSON.stringify(data), data.maxAgeSeconds);
}

/** Fixed window: `maxAgeSeconds` from the last capture. Not extended by activity/reloads. */
function isExpired(data: AttributionData): boolean {
  return Date.now() - data.lastAt > data.maxAgeSeconds * 1000;
}

/** Flatten first- + last-touch into the annotation/trait shape sent to analytics. */
function annotationFromData(data: AttributionData): CampaignParams {
  const annotation: CampaignParams = { ...data.last };
  for (const [key, value] of Object.entries(data.first)) {
    annotation[`${FIRST_TOUCH_PREFIX}${key}`] = value;
  }
  return annotation;
}

// ─── Public API ────────────────────────────────────────────────────────────────

export function applyCampaignAttribution(attrs: Record<string, string>): void {
  void withAnalytics(a => a.annotate(attrs));
}

/**
 * Capture campaign params: persist to the cookie (first-touch write-once,
 * last-touch overwritten) and annotate every event. A capture (a new UTM) starts
 * a fresh window (length = `windowSeconds`, from the latest `utm_window` param);
 * plain activity/reloads never extend it.
 */
function captureCampaignAttribution(attrs: CampaignParams, windowSeconds: number): void {
  const now = Date.now();
  const existing = readData();
  const data: AttributionData =
    existing && !isExpired(existing)
      ? // keep first-touch; refresh last-touch + the window from this capture
        { ...existing, last: attrs, lastAt: now, maxAgeSeconds: windowSeconds }
      : { first: attrs, last: attrs, firstAt: now, lastAt: now, maxAgeSeconds: windowSeconds };
  writeData(data);
  applyCampaignAttribution(annotationFromData(data));
}

/**
 * Capture UTMs from a query string when present (persisted, fixed window).
 * The window length honors an optional `utm_window` param (default 1 day).
 */
export function syncCampaignAttributionFromQueryString(queryString: string): void {
  const record = queryStringToRecord(queryString);
  const attrs = pickCampaignParams(record);
  if (attrs) captureCampaignAttribution(attrs, windowSecondsFromRecord(record));
}

/**
 * Capture UTMs from a full URL when present (persisted, fixed window).
 * The window length honors an optional `utm_window` param (default 1 day).
 */
export function syncCampaignAttributionFromUrl(url: string): void {
  const queryIndex = url.indexOf('?');
  if (queryIndex < 0) return;
  syncCampaignAttributionFromQueryString(url.slice(queryIndex + 1));
}

/**
 * Re-apply persisted campaign attribution on load (call once per page/tab).
 * This makes attribution survive reloads, new tabs, and return visits without
 * needing the UTMs in the URL — but only within the fixed 24h window from
 * capture. It does NOT extend the window (no activity-based sliding).
 */
export function rehydrateCampaignAttribution(): void {
  const data = readData();
  if (!data) return;
  if (isExpired(data)) {
    deleteRawCookie(COOKIE_NAME);
    return;
  }
  applyCampaignAttribution(annotationFromData(data));
}

/** First + last-touch params to stamp onto the user profile at login. */
export function getCampaignUserTraits(): Record<string, string> | null {
  const data = readData();
  if (!data || isExpired(data)) return null;
  const traits = annotationFromData(data);
  return Object.keys(traits).length > 0 ? traits : null;
}

/** Stamp the persisted campaign attribution onto the identified user's profile. */
export function stampCampaignAttributionOnUser(userId: string): void {
  const traits = getCampaignUserTraits();
  if (!traits) return;
  void withAnalytics(a => a.identify(userId, traits as any));
}

/** Clear persisted attribution + reset the annotation to the platform base. */
export function clearCampaignAttribution(): void {
  deleteRawCookie(COOKIE_NAME);
  void withAnalytics(a => a.replaceAnnotate({ ...BASE_ANNOTATION }));
}
